import { useState } from "react";
import { getRunSecret } from "../lib/api";

/**
 * Shows the one-liner to start a project-agent in a terminal, plus an
 * expandable first-time setup block (gh CLI + npmrc + OIDC creds + Claude CLI).
 *
 * Canonical setup as of 2026-05-22: GH Packages auth via `gh auth token` env
 * var (classic PATs revoked). Both `@theg1team` (legacy) + `@theg1group`
 * (canonical) registry scopes resolved through the same npm.pkg.github.com
 * with the gh-issued token. Dashboard is OIDC-gated — operators viewing this
 * page are authorized to know the creds for remote agent launch.
 *
 * The OIDC client secret is NOT baked into this bundle (B4-L). It's fetched on
 * demand from the operator-gated `/api/agents/run-secret` via the "Reveal run
 * secret" button and substituted into the setup script client-side.
 */
const SECRET_PLACEHOLDER = "<click 'Reveal run secret' to fetch>";

export function RunCommand({ label }: { label: string }) {
	const [secret, setSecret] = useState<string>(SECRET_PLACEHOLDER);
	const [revealing, setRevealing] = useState(false);
	const [revealErr, setRevealErr] = useState<string | null>(null);

	async function reveal() {
		setRevealing(true);
		setRevealErr(null);
		try {
			const { clientSecret } = await getRunSecret();
			setSecret(clientSecret);
		} catch (e) {
			setRevealErr(e instanceof Error ? e.message : "failed to fetch secret");
		} finally {
			setRevealing(false);
		}
	}

	const runCmd = `npx -y @theg1group/agent@1.0.0 run ${label}`;
	const setupCmd = `# === Per-machine, one-time ===

# 1. Install tools (macOS — adjust for Linux: nvm + apt + gh package)
brew install gh node@22 pnpm
brew install --cask claude

# 2. GitHub auth with read:packages (replaces revoked classic PATs)
gh auth login --scopes read:packages
# Your GitHub account must be a member of TheG1Group org with read access
# to @theg1group/* packages (Internal visibility). If 403 on next step,
# operator needs to invite you via https://github.com/orgs/TheG1Group/people

# 3. ~/.npmrc — both scopes, token from env var
cat > ~/.npmrc <<'EOF'
//npm.pkg.github.com/:_authToken=\${GH_PACKAGES_TOKEN}
@theg1team:registry=https://npm.pkg.github.com
@theg1group:registry=https://npm.pkg.github.com
EOF
chmod 600 ~/.npmrc

# 4. Sign into Claude Code CLI (uses your Claude Pro/Max subscription)
claude login

# 5. Persist GH_PACKAGES_TOKEN per shell session
echo 'export GH_PACKAGES_TOKEN=$(gh auth token 2>/dev/null)' >> ~/.zshrc
source ~/.zshrc

# === Configure OIDC credentials (interactive — runs once) ===

npx -y @theg1group/agent@1.0.0 setup
# Prompts:
#   OIDC_CLIENT_ID [g1-group-agent]:  <Enter — default is correct>
#   OIDC_CLIENT_SECRET:               ${secret}
#   API base URL [https://agent.g1.group]: <Enter>
# Saves to ~/.config/g1-agent/credentials.json (chmod 600)

# === Run (every shell) ===

${runCmd}

# ─── Alternative for headless / CI — skip 'setup' step, use env vars: ───
#   export G1_AGENT_CLIENT_ID="g1-group-agent"
#   export G1_AGENT_CLIENT_SECRET="${secret}"
#   (G1_AGENT_API_BASE_URL defaults to https://agent.g1.group)`;

	const [showSetup, setShowSetup] = useState(false);

	return (
		<div className="space-y-2">
			<CopyChip text={runCmd} display={`$ ${runCmd}`} hint="Run command — paste on any pre-configured machine" />
			<button
				type="button"
				onClick={() => setShowSetup((v) => !v)}
				className="text-[11px] text-zinc-500 hover:text-zinc-300 underline-offset-2 hover:underline"
			>
				{showSetup ? "Hide first-time setup" : "First time on this machine? Show setup"}
			</button>
			{showSetup && (
				<div className="space-y-1">
					<div className="flex items-center gap-2">
						<button
							type="button"
							onClick={reveal}
							disabled={revealing || secret !== SECRET_PLACEHOLDER}
							className="text-[11px] px-2 py-0.5 rounded border border-amber-700/60 bg-amber-950/30 text-amber-300 hover:border-amber-500 disabled:opacity-50"
						>
							{secret !== SECRET_PLACEHOLDER
								? "✓ run secret revealed"
								: revealing
									? "Revealing…"
									: "Reveal run secret"}
						</button>
						{revealErr && <span className="text-[11px] text-red-400">{revealErr}</span>}
					</div>
					<CopyBlock text={setupCmd} />
					<div className="text-[11px] text-zinc-600 leading-relaxed">
						<div>
							Prereqs: Node 20+, <code>gh</code> CLI logged in with{" "}
							<code>read:packages</code>, <code>claude</code> CLI signed into Claude
							subscription.
						</div>
						<div className="mt-1">
							The client secret is fetched on demand from the operator-gated{" "}
							<code>/api/agents/run-secret</code> — never baked into this page. Once
							revealed it still travels with the machine (<code>OIDC_CLIENT_SECRET</code>{" "}
							on the FS). Tracked as B4-L debt (awaiting RFC 8628 device flow); treat the
							machine like a production credential carrier.
						</div>
					</div>
				</div>
			)}
		</div>
	);
}

function CopyChip({ text, display, hint }: { text: string; display: string; hint: string }) {
	const [copied, setCopied] = useState(false);
	async function handleCopy() {
		try {
			await navigator.clipboard.writeText(text);
			setCopied(true);
			setTimeout(() => setCopied(false), 1500);
		} catch {
			/* noop — clipboard needs https/localhost */
		}
	}
	return (
		<button
			type="button"
			onClick={handleCopy}
			className="group inline-flex items-center gap-2 text-xs font-mono px-2 py-1 rounded border border-zinc-700 bg-terminal/60 hover:border-zinc-500 text-zinc-300"
			title={hint}
		>
			<span>{display}</span>
			<span className="text-zinc-500 group-hover:text-zinc-300">{copied ? "✓ copied" : "⧉"}</span>
		</button>
	);
}

function CopyBlock({ text }: { text: string }) {
	const [copied, setCopied] = useState(false);
	async function handleCopy() {
		try {
			await navigator.clipboard.writeText(text);
			setCopied(true);
			setTimeout(() => setCopied(false), 1500);
		} catch {
			/* noop */
		}
	}
	return (
		<div className="relative rounded border border-zinc-800 bg-terminal/70">
			<pre className="text-[11px] font-mono text-zinc-300 px-3 py-2 overflow-x-auto whitespace-pre">
				{text}
			</pre>
			<button
				type="button"
				onClick={handleCopy}
				className="absolute top-1.5 right-1.5 text-[11px] px-1.5 py-0.5 rounded border border-zinc-700 hover:border-zinc-500 bg-zinc-900/80 text-zinc-400 hover:text-zinc-200"
				title="Copy setup script"
			>
				{copied ? "✓" : "⧉ copy"}
			</button>
		</div>
	);
}
